"use client";

import { createRestDataProviderClient } from "@providers/data-provider/rest-data-provider.client";
import { isFilterValueExist, parseFilter, transformQueryParamsWithoutLocale } from "@shared/utils";
import {
  Button,
  Checkbox,
  ComboboxOption,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  SearchInput,
} from "@ubud-ui/react-ui-kit";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useRouter, useSearchParams } from "next/navigation";
import qs from "qs";
import { useEffect, useMemo, useState } from "react";

type Region = {
  id: string;
  name: string;
  type: "province" | "city" | "country";
};

const FILTER_KEY = "locations";

function LocationFilterDialog({ path }: { path: string }) {
  type FilterItem = {
    id: string;
    type: string;
    label: string;
  };

  const [isOpen, setIsOpen] = useState(false);

  const router = useRouter();
  const searchParams = useSearchParams();

  const restDataProviderClient = createRestDataProviderClient();

  const [locationKeyword, setLocationKeyword] = useState<string>("");

  const provincesQuery = useInfiniteQuery({
    queryKey: ["vacancy-location-filter-dialog-provinces", locationKeyword],
    queryFn: () =>
      restDataProviderClient.custom({
        url: transformQueryParamsWithoutLocale("/karirhub/onboarding/v2/provinces", {
          keyword: locationKeyword || undefined,
        }),
        method: "GET",
      }),
    getNextPageParam: (lastPage) => {
      const currentPage = lastPage.meta.currentPage;
      const lastPageNumber = lastPage.meta.lastPage;

      if (currentPage < lastPageNumber) {
        return currentPage + 1;
      }

      return undefined;
    },
    enabled: isOpen,
    initialPageParam: 1,
  });
  const citiesQuery = useInfiniteQuery({
    queryKey: ["vacancy-location-filter-dialog-cities", locationKeyword],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await restDataProviderClient.custom({
        url: transformQueryParamsWithoutLocale("/karirhub/onboarding/v2/cities", {
          keyword: locationKeyword || undefined,
          limit: 100,
          page: pageParam,
        }),
        method: "GET",
      });

      return response;
    },
    getNextPageParam: (lastPage) => {
      const currentPage = lastPage.meta?.pagination?.currentPage;
      const lastPageNumber = lastPage.meta?.pagination?.lastPage;

      if (currentPage < lastPageNumber) {
        return currentPage + 1;
      }

      return undefined;
    },
    enabled: isOpen,
    initialPageParam: 1,
  });

  const locationOptions = useMemo(() => {
    const provinces =
      provincesQuery.data?.pages.flatMap((page) =>
        (page.data || []).map((it: Region) => ({
          ...it,
          type: "province",
        })),
      ) ?? [];

    const cities =
      citiesQuery.data?.pages.flatMap((page) =>
        (page.data || []).map((it: Region) => ({
          ...it,
          type: "city",
        })),
      ) ?? [];

    return [...provinces, ...cities].map((region) => ({
      value: `${region.id}#${region.type}`,
      label: region.name,
    }));
  }, [provincesQuery.data, citiesQuery.data]);

  const groupedLocations = useMemo(() => {
    return [...locationOptions]
      .sort((a, b) => a.label.localeCompare(b.label, "id"))
      .reduce(
        (acc, option) => {
          const letter = option.label.charAt(0).toUpperCase();

          if (!acc[letter]) {
            acc[letter] = [];
          }

          acc[letter].push(option);

          return acc;
        },
        {} as Record<string, ComboboxOption[]>,
      );
  }, [locationOptions]);

  const handleLoadMore = () => {
    if (citiesQuery.hasNextPage) {
      citiesQuery.fetchNextPage();
    }

    if (provincesQuery.hasNextPage) {
      provincesQuery.fetchNextPage();
    }
  };

  const handleSearchLocations = (keyword: string) => {
    setLocationKeyword(keyword);
  };

  const [checkedItems, setCheckedItems] = useState<FilterItem[]>([]);

  useEffect(() => {
    if (!isOpen) return;

    const params = qs.parse(searchParams.toString());

    let locations = params[FILTER_KEY];

    if (!locations) {
      setCheckedItems([]);
      return;
    }

    if (!Array.isArray(locations)) {
      locations = [locations];
    }

    setCheckedItems(locations as FilterItem[]);
  }, [isOpen, searchParams]);

  const isChecked = (value: string) => {
    const [id] = value.split("#");

    return checkedItems.some((item) => item.id === id);
  };

  const handleFilter = (option: ComboboxOption, checked: boolean) => {
    const [id, type] = option.value.split("#");

    const item = {
      id,
      type,
      label: option.label,
    };

    if (checked) {
      setCheckedItems((prev) => {
        const exists = prev.some((it) => it.id === item.id);

        if (exists) return prev;

        return [...prev, item];
      });
    } else {
      setCheckedItems((prev) => prev.filter((it) => it.id !== item.id));
    }
  };

  const handleSubmitFilter = () => {
    const params = qs.parse(searchParams.toString());

    const nextParams = {
      ...params,
      [FILTER_KEY]: checkedItems.length > 0 ? checkedItems : undefined,
      page: undefined,
    };

    router.push(
      `${path}?${qs.stringify(nextParams, {
        skipNulls: true,
      })}`,
      {
        scroll: false,
      },
    );

    setIsOpen(false);
  };

  const handleCancel = () => {
    const params = qs.parse(searchParams.toString());

    let locations = params[FILTER_KEY] as FilterItem[];

    if (!locations) {
      setCheckedItems([]);
    } else {
      setCheckedItems(Array.isArray(locations) ? locations : [locations]);
    }

    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => setIsOpen(open)}>
      <DialogTrigger asChild>
        <Button variant="link" className="text-sm font-medium text-primary hover:underline m-0 p-0" type="button">
          Lihat Lebih Banyak
        </Button>
      </DialogTrigger>
      <DialogContent hideCloseButton>
        <DialogHeader className="gap-2">
          <div className="flex items-center gap-4 justify-between">
            <DialogTitle>Filter Lokasi</DialogTitle>
            <span className="text-gray-500 text-sm">Menampilkan {locationOptions.length} lokasi</span>
          </div>
          <div>
            <SearchInput placeholder="Cari lokasi" value={locationKeyword} onSearch={handleSearchLocations} />
          </div>
        </DialogHeader>

        <DialogBody>
          <div className="-mx-6">
            {locationOptions && locationOptions.length > 0 ? (
              <>
                <div className="space-y-5 pt-5 pb-2">
                  {Object.entries(groupedLocations).map(([letter, options]) => (
                    <div key={letter}>
                      <div className="sticky top-0 bg-white py-2 px-6 text-sm font-semibold text-gray-500 border-b">{letter}</div>

                      <div className="space-y-1 mt-3 px-3">
                        {options.map((option, index) => (
                          <div
                            className="flex items-start gap-3 hover:bg-gray-100 cursor-pointer rounded-lg"
                            key={`location-filter-dialog-${option.value}-${index}`}
                          >
                            <Checkbox
                              className="ml-3 my-3"
                              id={`location-filter-dialog-${option.value}-${index}`}
                              onCheckedChange={(checked) => handleFilter(option, checked as boolean)}
                              checked={isChecked(option.value)}
                            />

                            <label htmlFor={`location-filter-dialog-${option.value}-${index}`} className="block w-full cursor-pointer py-2">
                              {option.label}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                {citiesQuery.hasNextPage ? (
                  <div className="px-6">
                    <Button
                      variant="link"
                      className="text-sm font-medium text-primary hover:underline m-0 p-0"
                      type="button"
                      onClick={handleLoadMore}
                      disabled={citiesQuery.isFetchingNextPage}
                    >
                      {citiesQuery.isFetchingNextPage ? "Sedang memuat..." : "Tampilkan lebih banyak"}
                    </Button>
                  </div>
                ) : null}
              </>
            ) : (
              <div className="text-center font-bold pt-5 pb-2 min-h-[500px]">
                {!locationKeyword ? "Ketik untuk mencari lokasi" : "Lokasi tidak ditemukan"}
              </div>
            )}
          </div>
        </DialogBody>

        <DialogFooter>
          <div>
            <Button variant="outline" className="font-semibold w-full" onClick={handleCancel}>
              Batal
            </Button>
          </div>
          <div>
            <Button className="font-semibold w-full" onClick={handleSubmitFilter}>
              Terapkan
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function LocationFilter({ path }: { path: string }) {
  const router = useRouter();
  const searchParams = useSearchParams();

  const restDataProviderClient = createRestDataProviderClient();

  const [locationKeyword, setLocationKeyword] = useState<string>("");
  const { data: provincesResult } = useQuery({
    queryKey: ["vacancy-location-filter-provinces-", locationKeyword],
    queryFn: () =>
      restDataProviderClient.custom({
        url: transformQueryParamsWithoutLocale(
          "/karirhub/onboarding/v2/provinces",
          locationKeyword
            ? {
                keyword: locationKeyword,
                limit: 5,
              }
            : {
                limit: 5,
              },
        ),
        method: "GET",
      }),
    enabled: !!locationKeyword,
  });
  const { data: citiesResult } = useQuery({
    queryKey: ["vacancy-location-filter-cities-", locationKeyword],
    queryFn: () =>
      restDataProviderClient.custom({
        url: transformQueryParamsWithoutLocale(
          "/karirhub/onboarding/v2/cities",
          locationKeyword
            ? {
                keyword: locationKeyword,
                limit: 5,
              }
            : {
                limit: 5,
              },
        ),
        method: "GET",
      }),
  });
  const locationOptions: ComboboxOption[] =
    [
      ...(provincesResult?.data || []).map((it: Region) => {
        return { ...it, type: "province" };
      }),
      ...(citiesResult?.data || []).map((it: Region) => {
        return { ...it, type: "city" };
      }),
    ].map((region: Region) => ({
      value: `${region.id}#${region.type}`,
      label: region.name,
    })) ?? [];

  const handleSearchLocations = (keyword: string) => {
    setLocationKeyword(keyword);
  };

  const handleFilter = (option: ComboboxOption, checked: boolean) => {
    const paramsString = searchParams.toString();

    const locationValues = option && option.value ? option.value.split("#") : [];
    const value = {
      id: locationValues && locationValues.length > 0 ? locationValues[0] : null,
      type: locationValues && locationValues.length > 0 ? locationValues[1] : null,
      label: option.label,
    };

    const params = parseFilter(FILTER_KEY, value, qs.parse(paramsString), checked);

    router.push(`${path}?${qs.stringify(params, { skipNulls: true })}`, {
      scroll: false,
    });
  };

  const isChecked = (value: string) => {
    const params = qs.parse(searchParams.toString());
    const locationValues = value ? value.split("#") : [];

    return isFilterValueExist(params, FILTER_KEY, locationValues && locationValues.length > 0 ? locationValues[0] : undefined);
  };

  return (
    <>
      <div className="ml-px mt-px">
        <SearchInput placeholder="Cari lokasi" value={locationKeyword} onSearch={handleSearchLocations} />
      </div>
      {locationOptions && locationOptions.length > 0 ? (
        <>
          <div className="space-y-3 pt-5 pb-2">
            {locationOptions.map((option, index) => {
              return (
                <div className="flex items-start gap-3" key={option.value + "-" + index}>
                  {isChecked(option.value)}
                  <Checkbox
                    className="mt-1"
                    id={option.value + "-" + index}
                    onCheckedChange={(checked) => handleFilter(option, checked as boolean)}
                    checked={isChecked(option.value)}
                  />
                  <label htmlFor={option.value + "-" + index}>{option.label}</label>
                </div>
              );
            })}
          </div>
          <LocationFilterDialog path={path} />
        </>
      ) : (
        <div className="text-center font-bold pt-5 pb-2">{!locationKeyword ? "Ketik untuk mencari lokasi" : "Lokasi tidak ditemukan"}</div>
      )}
    </>
  );
}
